import React from "react";
import { Tooltip } from "@/components/common/Tooltip";

export interface SegmentedControlOption<T> {
  value: T;
  label: string;
  /** Kept in place, greyed; `hint` names the reason on hover. */
  disabled?: boolean;
  hint?: string;
  /** A quiet number after the label, such as how many the option holds. */
  count?: number;
}

export interface SegmentedControlProps<T> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (v: T) => void;
  /** If set, shows a green dot on the active option when it matches autoValue */
  autoValue?: T | null;
}

export function SegmentedControl<T>({
  options,
  value,
  onChange,
  autoValue,
}: SegmentedControlProps<T>) {
  return (
    <div className="inline-flex h-full border border-border rounded-app overflow-hidden">
      {options.map((opt, i) => {
        const isActive = opt.value === value;
        const isAuto = isActive && autoValue != null && opt.value === autoValue;
        const tone = opt.disabled
          ? `bg-transparent text-fg-muted/50 ${opt.hint ? "cursor-pointer" : "cursor-not-allowed"}`
          : isActive
            ? "bg-accent-subtle text-accent font-medium cursor-pointer"
            : "bg-transparent text-fg-muted hover:bg-bg-hover hover:text-fg cursor-pointer";
        const button = (
          <button
            type="button"
            onClick={() => !opt.disabled && onChange(opt.value)}
            aria-pressed={isActive}
            aria-disabled={opt.disabled || undefined}
            aria-label={opt.hint ? opt.label : undefined}
            // The invisible bold copy holds the width, so selecting never resizes the control.
            data-label={opt.label}
            className={`h-full px-2 text-(length:--font-size) whitespace-nowrap transition-colors duration-100 after:block after:h-0 after:overflow-hidden after:invisible after:font-medium after:content-[attr(data-label)] ${tone}`}
          >
            {opt.label}
            {opt.count !== undefined && (
              <span className="ml-1 font-mono font-normal text-(length:--font-size-small) tabular-nums">
                {opt.count}
              </span>
            )}
            {isAuto && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-success ml-1 align-middle relative -top-px" />
            )}
          </button>
        );
        return (
          <span
            key={String(opt.value)}
            className={`flex ${i < options.length - 1 ? "border-r border-border" : ""}`}
          >
            {opt.disabled && opt.hint ? (
              <Tooltip title={opt.hint} placement="top">
                {button}
              </Tooltip>
            ) : (
              button
            )}
          </span>
        );
      })}
    </div>
  );
}
